import React, { useState, useEffect } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNav } from "../../components/NavContext";

const LocationReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [reportTypeFilter, setReportTypeFilter] = useState("");
  const [applyingReportId, setApplyingReportId] = useState(null);

  const DOMAIN = process.env.REACT_APP_DOMAIN;
  const API_URL = process.env.REACT_APP_API_URL;

  const { addLink } = useNav();

  useEffect(() => {
    addLink("اطبائنا", `${DOMAIN}control/doctor-list`);
    addLink("Location Reports", `${DOMAIN}control/admin/location-reports`);
  }, [addLink, DOMAIN]);

  useEffect(() => {
    document.title = "Location Reports";
  }, []);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, reportTypeFilter, perPage]);

  useEffect(() => {
    fetchReports();
  }, [page, perPage, statusFilter, reportTypeFilter]);

  const fetchReports = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('per_page', String(perPage));
      if (statusFilter) params.set('status', statusFilter);
      if (reportTypeFilter) params.set('report_type', reportTypeFilter);

      const response = await axios.get(`${API_URL}location-reports?${params.toString()}`, {
        withCredentials: true,
      });

      setReports(response.data.data || []);
      setMeta({
        current_page: response.data.current_page || 1,
        last_page: response.data.last_page || 1,
        per_page: response.data.per_page || perPage,
        total: response.data.total || 0,
      });
    } catch (e) {
      console.error("Error fetching reports:", e);
      setError("فشل تحميل التقارير");
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyReport = async (reportId) => {
    if (!window.confirm("هل أنت متأكد من تطبيق هذه التصحيحات على الموقع؟")) {
      return;
    }

    setApplyingReportId(reportId);

    try {
      await axios.post(`${API_URL}location-reports/${reportId}/apply`, {}, {
        withCredentials: true,
      });

      alert("تم تطبيق التصحيحات بنجاح");
      fetchReports();
    } catch (e) {
      console.error("Error applying report:", e);
      alert(e.response?.data?.message || "فشل في تطبيق التصحيحات");
    } finally {
      setApplyingReportId(null);
    }
  };

  const getReportTypeLabel = (type) => {
    switch (type) {
      case 'closed': return 'مغلق';
      case 'relocated': return 'تم النقل';
      case 'wrong_information': return 'معلومات خاطئة';
      default: return type;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'bg-warning text-dark';
      case 'reviewed': return 'bg-info';
      case 'resolved': return 'bg-success';
      case 'rejected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'قيد الانتظار';
      case 'reviewed': return 'تمت المراجعة';
      case 'resolved': return 'تم الحل';
      case 'rejected': return 'مرفوض';
      default: return status;
    }
  };

  const hasChanges = (report) => {
    if (report.report_type !== 'wrong_information') return false;
    return report.corrected_name || report.corrected_category || report.corrected_phone;
  };

  const renderChangeHighlight = (label, oldValue, newValue, changed) => {
    if (!newValue) return null;

    return (
      <div className={`mb-2 p-2 rounded ${changed ? 'bg-warning bg-opacity-25 border border-warning' : 'bg-light'}`}>
        <strong>{label}:</strong>
        {changed && (
          <div className="small">
            <span className="text-muted text-decoration-line-through">{oldValue || 'N/A'}</span>
            {' → '}
            <span className="text-success fw-bold">{newValue}</span>
          </div>
        )}
        {!changed && (
          <div className="small text-muted">
            {newValue} <span className="badge bg-secondary ms-1">لا تغيير</span>
          </div>
        )}
      </div>
    );
  };

  if (loading && reports.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4" dir="rtl">
      <header className="mb-4 text-center">
        <h1 className="display-6 mb-1">تقارير المواقع</h1>
        <p className="text-muted mb-0">إدارة تقارير المستخدمين عن المواقع</p>
      </header>

      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">جميع الحالات</option>
            <option value="pending">قيد الانتظار</option>
            <option value="reviewed">تمت المراجعة</option>
            <option value="resolved">تم الحل</option>
            <option value="rejected">مرفوض</option>
          </select>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={reportTypeFilter}
            onChange={(e) => setReportTypeFilter(e.target.value)}
          >
            <option value="">جميع الأنواع</option>
            <option value="closed">مغلق</option>
            <option value="relocated">تم النقل</option>
            <option value="wrong_information">معلومات خاطئة</option>
          </select>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={perPage}
            onChange={(e) => setPerPage(parseInt(e.target.value, 10) || 20)}
          >
            <option value={10}>10 لكل صفحة</option>
            <option value={20}>20 لكل صفحة</option>
            <option value={50}>50 لكل صفحة</option>
            <option value={100}>100 لكل صفحة</option>
          </select>
        </div>
        <div className="col-md-3 text-start">
          <button className="btn btn-primary w-100" onClick={fetchReports} disabled={loading}>
            {loading ? 'جاري التحميل...' : 'تحديث'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row g-3">
        {reports && reports.length > 0 ? (
          reports.map((report) => {
            const nameChanged = report.corrected_name && report.corrected_name !== report.location?.name;
            const categoryChanged = report.corrected_category && report.corrected_category != report.location?.location_type_id;
            const phoneChanged = report.corrected_phone && report.corrected_phone !== report.location?.phone;

            return (
              <div key={report.id} className="col-12">
                <div className="card shadow-sm">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-0">
                        {report.location?.name || 'موقع غير معروف'}
                      </h5>
                      <small className="text-muted">
                        التقرير #{report.id} - {new Date(report.created_at).toLocaleDateString('ar-IQ')}
                        {report.user?.name && ` | بواسطة: ${report.user.name}`}
                      </small>
                    </div>
                    <div className="d-flex gap-2">
                      <span className={`badge ${getStatusBadgeClass(report.status)}`}>
                        {getStatusLabel(report.status)}
                      </span>
                      <span className="badge bg-secondary">
                        {getReportTypeLabel(report.report_type)}
                      </span>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <h6 className="fw-bold mb-2">معلومات المستخدم:</h6>
                        <p className="mb-1">
                          <strong>الاسم:</strong> {report.user?.name || 'N/A'}
                        </p>
                        <p className="mb-1">
                          <strong>الهاتف:</strong> {report.user?.phone || 'N/A'}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <h6 className="fw-bold mb-2">معلومات الموقع الحالية:</h6>
                        <p className="mb-1">
                          <strong>الاسم:</strong> {report.location?.name || 'N/A'}
                        </p>
                        <p className="mb-1">
                          <strong>الفئة ID:</strong> {report.location?.location_type_id || 'N/A'}
                        </p>
                        <p className="mb-1">
                          <strong>الهاتف:</strong> {report.location?.phone || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {report.report_type === 'wrong_information' && hasChanges(report) && (
                      <div className="mt-3">
                        <h6 className="fw-bold mb-2">التصحيحات المقترحة:</h6>
                        {renderChangeHighlight(
                          'الاسم',
                          report.location?.name,
                          report.corrected_name,
                          nameChanged
                        )}
                        {renderChangeHighlight(
                          'الفئة',
                          report.location?.location_type_id,
                          report.corrected_category,
                          categoryChanged
                        )}
                        {renderChangeHighlight(
                          'رقم الهاتف',
                          report.location?.phone,
                          report.corrected_phone,
                          phoneChanged
                        )}
                      </div>
                    )}

                    {report.note && (
                      <div className="mt-3">
                        <h6 className="fw-bold mb-2">ملاحظة:</h6>
                        <p className="text-muted mb-0">{report.note}</p>
                      </div>
                    )}

                    {report.reviewed_at && (
                      <div className="mt-3">
                        <small className="text-muted">
                          تمت المراجعة في: {new Date(report.reviewed_at).toLocaleString('ar-IQ')}
                          {report.reviewer && ` بواسطة: ${report.reviewer.name}`}
                        </small>
                      </div>
                    )}
                  </div>
                  {report.report_type === 'wrong_information' && 
                   report.status !== 'resolved' && 
                   hasChanges(report) && (
                    <div className="card-footer text-end">
                      <button
                        className="btn btn-success"
                        onClick={() => handleApplyReport(report.id)}
                        disabled={applyingReportId === report.id}
                      >
                        {applyingReportId === report.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            جاري التطبيق...
                          </>
                        ) : (
                          'تطبيق التصحيحات'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-12">
            <div className="alert alert-info text-center">
              لا توجد تقارير لعرضها
            </div>
          </div>
        )}
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-2">
        <div className="text-muted">
          الإجمالي: {meta.total ?? 0}
        </div>

        <nav aria-label="Reports pagination">
          <ul className="pagination mb-0">
            <li className={`page-item ${meta.current_page <= 1 ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                السابق
              </button>
            </li>

            <li className="page-item disabled">
              <span className="page-link">
                صفحة {meta.current_page} من {meta.last_page}
              </span>
            </li>

            <li className={`page-item ${meta.current_page >= meta.last_page ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
              >
                التالي
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default LocationReports;
